import { createLoader, createParser, parseAsInteger, parseAsString } from 'nuqs/server';
import { invert } from 'lodash-es';

export type ListFilterOdometer = `${number}` | 'MAX';

export enum EntryListFilterType {
  UsedAndNew = 'kaikki',
  OnlyUsed = 'kaytetyt',
  OnlyNew = 'uudet',
}

export enum SellerType {
  All = 0,
  Companies = 1,
  Private = 2,
}

export interface EntryListFilter {
  readonly soldForHighestBidder?: boolean;
  readonly onlyEnded?: boolean;
  readonly region?: string;
  readonly category?: number;
  readonly searchTerm?: string;
  readonly class?: string;
  readonly company?: number;
  readonly metadataManufacturer?: string;
  readonly metadataDrive?: number;
  readonly metadataOdometer?: ListFilterOdometer;
  readonly metadataTransmission?: number;
  readonly metadataFuelType?: number;
  readonly metadataYearModelStart?: number;
  readonly metadataYearModelEnd?: number;
  readonly type?: EntryListFilterType;
  readonly isFastBidPeriod?: boolean;
  readonly groupId?: number;
  readonly showPrivateAuctions?: boolean;
  readonly sellerType?: SellerType;
}

export const filterToQueryParam: Record<keyof EntryListFilter, string> = {
  soldForHighestBidder: 'met',
  onlyEnded: 'paattyneet',
  region: 'region',
  type: 'tyyppi',
  searchTerm: 'term',
  category: 'category',
  class: 'class',
  company: 'company',
  metadataManufacturer: 'merkki',
  metadataDrive: 'vetotapa',
  metadataOdometer: 'km',
  metadataTransmission: 'vaihteisto',
  metadataFuelType: 'polttoaine',
  metadataYearModelStart: 'vuosimalliMin',
  metadataYearModelEnd: 'vuosimalliMax',
  isFastBidPeriod: 'isFastBidPeriod',
  groupId: 'groupId',
  showPrivateAuctions: 'showPrivateAuctions',
  sellerType: 'sellerType',
};

export enum EntryListSort {
  Ending = 'paattyvat',
  MostViewed = 'katsotuimmat',
  Newest = 'uusimmat',
}

export interface EntryListQuery {
  readonly filter: EntryListFilter;
  readonly sort: EntryListSort;
  readonly page?: number;
  readonly limit?: number;
}

export const paramsTypeToQueryType: Record<string, EntryListFilterType> = {
  kaikki: EntryListFilterType.UsedAndNew,
  kaytetyt: EntryListFilterType.OnlyUsed,
  uudet: EntryListFilterType.OnlyNew,
};

export const paramsSortToQuerySort: Record<string, EntryListSort> = {
  paattyvat: EntryListSort.Ending,
  katsotuimmat: EntryListSort.MostViewed,
  uusimmat: EntryListSort.Newest,
};

export const sortTabToQuerySort: Record<number, EntryListSort> = {
  0: EntryListSort.Ending,
  1: EntryListSort.MostViewed,
  2: EntryListSort.Newest,
};

export const querySortToParamsSort = invert(paramsSortToQuerySort);
export const querySortToSortTab = invert(sortTabToQuerySort);
export const queryTypeToParamsType = invert(paramsTypeToQueryType);

export const searchParamsParsers = {
  region: parseAsString,
  tyyppi: createParser({
    parse: (value) => paramsTypeToQueryType[value] ?? null,
    serialize: (value: EntryListFilterType) => queryTypeToParamsType[value],
  }),
  met: createParser({
    parse: (value) => value === '1',
    serialize: (value) => (value ? '1' : '0'),
  }),
  paattyneet: createParser({
    parse: (value) => value === '1',
    serialize: (value) => (value ? '1' : '0'),
  }),
  merkki: parseAsString,
  vetotapa: parseAsInteger,
  km: createParser({
    parse: (value) => (/^\d+$/.test(value) || value === 'MAX' ? (value as ListFilterOdometer) : null),
    serialize: (value) => String(value),
  }),
  vaihteisto: parseAsInteger,
  polttoaine: parseAsInteger,
  vuosimalliMin: parseAsInteger,
  vuosimalliMax: parseAsInteger,
  sellerType: parseAsInteger,
  jarjestys: createParser({
    parse: (value) => paramsSortToQuerySort[value] ?? null,
    serialize: (value) => querySortToParamsSort[value],
  }),
  sivu: parseAsInteger,
};

export const loadSearchParams = createLoader(searchParamsParsers);

export const paramsToQuery = (
  params: Awaited<ReturnType<typeof loadSearchParams>>,
  baseQuery: Partial<EntryListQuery> = {},
  categoryId?: number
) => ({
  filter: {
    ...baseQuery?.filter,
    category: categoryId,
    region: params.region ?? undefined,
    type: params.tyyppi ?? undefined,
    soldForHighestBidder: params.met ?? undefined,
    onlyEnded: params.paattyneet ?? undefined,
    metadataManufacturer: params.merkki ?? undefined,
    metadataDrive: params.vetotapa ?? undefined,
    metadataOdometer: params.km ?? undefined,
    metadataTransmission: params.vaihteisto ?? undefined,
    metadataFuelType: params.polttoaine ?? undefined,
    metadataYearModelStart: params.vuosimalliMin ?? undefined,
    metadataYearModelEnd: params.vuosimalliMax ?? undefined,
    sellerType: params.sellerType ?? undefined,
  },
  sort: params.jarjestys ?? EntryListSort.Ending,
  page: params.sivu ?? 1,
  limit: 40,
});
